import { useMemo } from 'react';
import type { SurveyQuestion } from '@hotjar/feedback/domain/Surveys/Questions';
import type { AggregatedResult } from '@hotjar/feedback/domain/Surveys/Results/AggregatedResult';
import { AnswersBarChart } from '@hotjar/webapp/features/Surveys/Results/AnswersBarChart';
import { max, includes, each, keys, values } from 'lodash-es';
import {
  AnswersTable,
  toLetter,
  MultiAnswersTable,
  RatingScaleAnswersTable,
} from '../AnswersTable';

interface BarChartWithTableProps {
  result: AggregatedResult;
  questions: SurveyQuestion[];
  selectedQuestionUuid: string;
}

export const BarChartWithTable = ({
  result,
  questions,
  selectedQuestionUuid,
}: BarChartWithTableProps): JSX.Element | null => {
  const question = questions.find((q) => q.uuid === selectedQuestionUuid);

  const [chartData, highestResponseCount, usePercentages] = useMemo(() => {
    if (result.answers.length > 10) return [null, null, null];

    if (!question) return [null, null, null];

    // Change object reference to force React rerender.
    const labels: string[] = [];
    const dataset: number[] = [];

    // Total count for each answer.
    const countsByResponse = result.answers;

    // Percentages for each answer.
    const percentagesByResponse = result.pc_answers;

    // Highest count we have for a single answer.
    const highestCount = max(values(countsByResponse));

    let usePercent = false;

    if (includes(['rating-scale-5', 'rating-scale-7'], question.type)) {
      each(keys(countsByResponse), (answer) => {
        // Values for chart.
        labels.push(answer);
        dataset.push(percentagesByResponse[answer] || 0);
      });

      usePercent = true;
    } else if (includes(['multiple-close-ended', 'single-close-ended'], question.type)) {
      // Reorder the responses based on how they appear in the poll.
      // Also assign a letter per answer (A,B,C,D...)
      each(keys(countsByResponse), (answer, i) => {
        // Values for chart.
        labels.push(toLetter(i));
        dataset.push(countsByResponse[answer] || 0);
      });

      usePercent = false;
    }

    return [{ labels, dataset }, highestCount, usePercent];
  }, [result, question]);

  if (question === undefined) return null;

  return (
    <div className="bar-chart-with-table-container">
      <div className="bar-chart-with-table-table">
        {question.type === 'multiple-close-ended' && (
          <MultiAnswersTable result={result} showIndex={!!chartData} />
        )}
        {question.type === 'single-close-ended' && (
          <AnswersTable result={result} showIndex={!!chartData} />
        )}
        {question.type !== 'multiple-close-ended' && question.type !== 'single-close-ended' && (
          <RatingScaleAnswersTable result={result} question={question} />
        )}
      </div>
      {chartData && highestResponseCount !== undefined && usePercentages !== null && (
        <div className="bar-chart-with-table-chart">
          <AnswersBarChart
            chartData={chartData}
            highestValue={highestResponseCount}
            usePercentages={usePercentages}
          />
        </div>
      )}
    </div>
  );
};
